import { Auth } from 'aws-amplify';
import { Base64 } from 'js-base64';
import React, { useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Location, useLocation, useNavigate } from 'react-router-dom';

import Form, { FormData } from '../../../components/Form/Form';
import { useAppContext } from '../../../contexts/AppContext';
import { useExtendedTranslation } from '../../../hooks/useExtendedTranslation';
import AgreementFooter from '../../../overwrites/views/auth/shared/AgreementFooter/AgreementFooter';
import { EmailRegex, querystring } from '../../../utils/RouteHelper';
import { FormContainer } from '../auth.styles';
import SignInHeader from '../shared/SignInHeader';
import { Invite, InviteBlack, SignUpButton, StyledUnauthenticatedLayout, UserNameContainer } from './SignupPage.styles';

export interface SignupStateProps {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  inviterName?: string;
  inviterOrganization?: string;
  inviteQueryParam?: string;
}

enum FormKey {
  FirstName = 'firstName',
  LastName = 'lastName',
  Email = 'email',
  Phone = 'phone',
}

const Signup = () => {
  const { state, search } = useLocation() as Location & { state: SignupStateProps };
  const [inviterName, setInviterName] = React.useState<string | undefined>(state?.inviterName || '');
  const [inviterOrganization, setInviterOrganization] = React.useState<string | undefined>(
    state?.inviterOrganization || '',
  );
  const [disabledEmail, setDisabledEmail] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const formRef = useRef(null);
  const [formData, setFormData] = useState<FormData>({
    [FormKey.FirstName]: state?.firstName,
    [FormKey.LastName]: state?.lastName,
    [FormKey.Email]: state?.email,
    [FormKey.Phone]: state?.phone,
  });

  const [submitted, setSubmitted] = useState(false);

  const navigate = useNavigate();
  const { t, tSpecific } = useExtendedTranslation();
  const { showError } = useAppContext();

  const validate = (data: FormData, errors: any) => {
    const email = data[FormKey.Email] as string;
    if (!EmailRegex.test(email)) {
      errors[FormKey.Email] = t('auth.error.invalidEmail');
      return false;
    }

    return true;
  };

  const handleSubmit = (data: FormData) => {
    const sanitizedEmail = (data[FormKey.Email] as string).toLowerCase().trim();
    void (async () => {
      try {
        setLoading(true);
        await Auth.signIn(sanitizedEmail, '1');
      } catch (err: any) {
        if (err.code === 'UserNotConfirmedException') {
          navigate('/login', { state: { email: sanitizedEmail } });
          showError(t('auth.error.userAlreadyExists'));
          return;
        }
        if (err.code === 'NotAuthorizedException') {
          showError(t('auth.error.userAlreadyExists'));
          return;
        } else if (err.code === 'UserNotFoundException') {
          console.info('email available');
        }

        setSubmitted(true);
        navigate('/signup-confirm', {
          state: {
            firstName: data[FormKey.FirstName],
            lastName: data[FormKey.LastName],
            email: sanitizedEmail,
            phone: data[FormKey.Phone],
            inviterOrganization,
            inviterName,
            // We pass the query param to the next page so we can use it if the user goes back to this page.
            inviteQueryParam: querystring('invite', search),
          },
        });
      } finally {
        setLoading(false);
      }
    })();
  };

  React.useEffect((): any => {
    const populateEmail = (email: string | null | undefined) => {
      if (email) {
        setFormData({ ...formData, [FormKey.Email]: email });
        setDisabledEmail(true);
      }
    };

    const isSignUpAvailable = process.env.REACT_APP_SIGNUP_WITHOUT_INVITE_ENABLE === 'true';
    const paramInvite = querystring('invite', search);
    if (paramInvite) {
      try {
        const invite: { email: string; inviter: string; org: string } = JSON.parse(Base64.decode(paramInvite));
        populateEmail(invite?.email);
        setInviterName(invite?.inviter);
        setInviterOrganization(invite?.org);
      } catch {
        if (!isSignUpAvailable) {
          // If invite is invalid, and we're not allowing signups
          navigate('/login', { replace: true });
        }
      }
    } else if (!submitted && !isSignUpAvailable) {
      // If no invite and no signup available, redirect to login
      // We need to check if the form was not submitted because if not, we will redirect to login when submitting
      navigate('/login', { replace: true });
    }

    const paramEmail = querystring('email', search);
    populateEmail(paramEmail);
  }, [search]);

  return (
    <StyledUnauthenticatedLayout title={tSpecific('auth.signUp.title')} header={<SignInHeader />}>
      <Form ref={formRef} data={formData} validateFn={validate} onSubmit={handleSubmit}>
        {({ input, phone }) => (
          <FormContainer>
            {inviterName && inviterOrganization && (
              <Invite>
                <Trans
                  i18nKey="auth.signUp.invite"
                  values={{
                    inviter: inviterName,
                    organization: inviterOrganization,
                  }}
                  components={{ black: <InviteBlack /> }}
                />
              </Invite>
            )}
            <UserNameContainer>
              {input({
                dataKey: FormKey.FirstName,
                required: true,
                errorRequired: t('auth.error.firstName'),
                label: t('auth.common.firstName'),
                placeholder: t('auth.signUp.firstName.placeholder'),
                autoComplete: 'first-name',
                name: 'firstName',
                autoFocus: true,
                allowOnlyLetters: true,
              })}
              {input({
                dataKey: FormKey.LastName,
                required: true,
                errorRequired: t('auth.error.lastName'),
                label: t('auth.common.lastName'),
                placeholder: t('auth.signUp.lastName.placeholder'),
                autoComplete: 'family-name',
                name: 'lastName',
                autoFocus: true,
                allowOnlyLetters: true,
              })}
            </UserNameContainer>
            {input({
              dataKey: FormKey.Email,
              required: true,
              label: t('auth.common.email.label'),
              errorRequired: t('auth.error.email'),
              placeholder: t('auth.common.email.placeholder'),
              name: 'email',
              autoComplete: 'email',
              disabled: disabledEmail,
            })}
            {phone({
              dataKey: FormKey.Phone,
              required: true,
              errorRequired: t('auth.error.phone'),
              country: 'us',
            })}
            <SignUpButton loading={loading} onPress={() => formRef.current.submit()}>
              {t('auth.common.signUp')}
            </SignUpButton>
            <AgreementFooter />
          </FormContainer>
        )}
      </Form>
    </StyledUnauthenticatedLayout>
  );
};

export default Signup;
